import {
  defineComponent,
  h,
  inject,
  onBeforeUnmount,
  onMounted,
  ref,
  shallowRef,
  type Slots,
  Teleport,
  type VNodeChild,
  watch,
} from 'vue';
import type { DefineComponent, PropType } from 'vue';

import {
  CodeView as CodeViewClass,
  type CodeViewCoordinator,
  type CodeViewLineSelection,
  type CodeViewOptions,
  type CodeViewRenderedItem,
} from '../components/CodeView';
import {
  CUSTOM_HEADER_SLOT_ID,
  HEADER_METADATA_SLOT_ID,
  HEADER_PREFIX_SLOT_ID,
} from '../constants';
import type { CodeViewItem, CodeViewScrollTarget } from '../types';
import { areOptionsEqual } from '../utils/areOptionsEqual';
import { getLineAnnotationName } from '../utils/getLineAnnotationName';
import { GutterUtilitySlotStyles, noopRender } from './constants';
import { WorkerPoolInjectionKey } from './injection';
import type { CodeViewPropsVue } from './types';

interface CodeViewCache {
  items: readonly CodeViewItem[] | undefined;
  controlled: boolean | undefined;
  managedOptions: CodeViewOptions<undefined> | undefined;
  coordinatorKey: string | undefined;
  coordinator: CodeViewCoordinator<undefined> | undefined;
}

// Renders a mixed, virtualized list of files and diffs. Unlike the light-DOM
// components, slot content must be projected into the per-item `<diffs-container>`
// elements the core creates as they scroll into view. A `slotCoordinator`
// publishes the set of currently rendered items (each with its element) into a
// reactive snapshot, and we render one `<Teleport>` per item into its element.
// Because the core constructs the CodeView with `isContainerManaged = true`, it
// never clears the light-DOM children Vue projects; an element only returns to
// the pool once it is clean (`pendingElementPool`), which tolerates Vue's
// asynchronous teleport teardown.
const CodeViewImpl = defineComponent({
  name: 'PierreCodeView',
  inheritAttrs: false,
  props: {
    items: {
      type: Array as PropType<readonly CodeViewItem[]>,
      default: undefined,
    },
    initialItems: {
      type: Array as PropType<readonly CodeViewItem[]>,
      default: undefined,
    },
    options: {
      type: Object as PropType<CodeViewOptions<undefined>>,
      default: undefined,
    },
    selectedLines: {
      type: Object as PropType<CodeViewLineSelection | null>,
      default: undefined,
    },
    disableWorkerPool: { type: Boolean, default: false },
  },
  emits: ['selectedLinesChange', 'scroll'],
  setup(props, { slots, emit, expose, attrs }) {
    const poolManager = inject(WorkerPoolInjectionKey, undefined);
    const containerRef = ref<HTMLElement | null>(null);
    // The currently rendered items, published by the core's slot coordinator and
    // consumed by the render function to drive one `<Teleport>` per item.
    const snapshot = shallowRef<CodeViewRenderedItem<undefined>[]>([]);
    // Stable identity so reconciled option objects compare equal unless the
    // user-supplied options actually change.
    const emitSelectedLinesChange = (
      selection: CodeViewLineSelection | null
    ): void => {
      emit('selectedLinesChange', selection);
    };
    let instance: CodeViewClass | undefined;
    let unsubscribeScroll: (() => void) | undefined;

    const cache: CodeViewCache = {
      items: undefined,
      controlled: undefined,
      managedOptions: undefined,
      coordinatorKey: undefined,
      coordinator: undefined,
    };

    function onSnapshotChange(
      next: CodeViewRenderedItem<undefined>[] | undefined
    ): void {
      snapshot.value = next ?? [];
    }

    // Rebuild the slot coordinator only when the set of active slots changes, so
    // its identity stays stable across renders (the core treats a new
    // coordinator identity as a reason to re-publish).
    function getSlotCoordinator(): CodeViewCoordinator<undefined> | undefined {
      const hasHeaderRenderers =
        slots.header != null ||
        slots.headerPrefix != null ||
        slots.headerMetadata != null;
      const hasAnnotationRenderer = slots.annotation != null;
      const hasGutterRenderer = slots.gutter != null;
      if (!hasHeaderRenderers && !hasAnnotationRenderer && !hasGutterRenderer) {
        cache.coordinatorKey = 'none';
        cache.coordinator = undefined;
        return undefined;
      }
      const key = `${hasHeaderRenderers}:${hasAnnotationRenderer}:${hasGutterRenderer}`;
      if (cache.coordinatorKey !== key || cache.coordinator == null) {
        cache.coordinatorKey = key;
        cache.coordinator = {
          hasHeaderRenderers,
          hasAnnotationRenderer,
          hasGutterRenderer,
          onSnapshotChange,
        };
      }
      return cache.coordinator;
    }

    function buildManagedOptions(): CodeViewOptions<undefined> | undefined {
      return createManagedCodeViewOptions({
        options: props.options,
        hasCustomHeader: slots.header != null,
        hasGutterRenderer: slots.gutter != null,
        onSelectedLinesChange: emitSelectedLinesChange,
        controlledSelection: props.selectedLines !== undefined,
      });
    }

    function reconcile(): void {
      if (instance == null) {
        return;
      }
      const managedOptions = buildManagedOptions();
      let shouldRender = false;

      if (!areOptionsEqual(managedOptions, cache.managedOptions)) {
        cache.managedOptions = managedOptions;
        instance.setOptions(managedOptions);
        shouldRender = true;
      }

      const controlled = props.items !== undefined;
      if (cache.controlled !== undefined && cache.controlled !== controlled) {
        console.error(
          'CodeView: cannot switch between controlled and uncontrolled modes. Remount with a new key instead.'
        );
        return;
      }
      cache.controlled = controlled;

      if (controlled) {
        const controlledItems = props.items;
        const prevItems = cache.items;
        if (controlledItems !== prevItems) {
          if (areItemListsEqual(prevItems, controlledItems)) {
            cache.items = controlledItems;
          } else if (isAppendOnlyItemUpdate(prevItems, controlledItems)) {
            cache.items = controlledItems;
            instance.addItems(controlledItems.slice(prevItems.length));
          } else {
            cache.items = controlledItems;
            instance.setItems(controlledItems);
            shouldRender = true;
          }
        }
      } else if (cache.items == null) {
        // Uncontrolled: seed once from initialItems; later updates go through the
        // exposed ref API.
        const seedItems = props.initialItems ?? [];
        cache.items = seedItems;
        if (seedItems.length > 0) {
          instance.setItems(seedItems);
          shouldRender = true;
        }
      }

      if (props.selectedLines !== undefined) {
        instance.setSelectedLines(props.selectedLines, { notify: false });
      }

      const slotPublish = instance.setSlotCoordinator(getSlotCoordinator());
      if (shouldRender || slotPublish) {
        instance.render(true);
      }
    }

    onMounted(() => {
      const root = containerRef.value;
      if (root == null) {
        throw new Error('CodeView: container element missing on mount');
      }
      cache.managedOptions = buildManagedOptions();
      instance = new CodeViewClass(
        cache.managedOptions,
        props.disableWorkerPool ? undefined : poolManager,
        true
      );
      instance.setup(root);
      unsubscribeScroll = instance.subscribeToScroll((scrollTop, viewer) => {
        emit('scroll', scrollTop, viewer);
      });
      reconcile();
    });

    watch(
      () =>
        [
          props.items,
          props.options,
          props.selectedLines,
          slots.header != null,
          slots.headerPrefix != null,
          slots.headerMetadata != null,
          slots.annotation != null,
          slots.gutter != null,
        ] as const,
      () => reconcile(),
      { flush: 'post' }
    );

    onBeforeUnmount(() => {
      unsubscribeScroll?.();
      instance?.cleanUp();
      instance = undefined;
    });

    expose({
      addItems(items: readonly CodeViewItem[]) {
        assertUncontrolledCodeViewAction(cache.controlled, 'addItems');
        if (instance == null) {
          console.error(
            'CodeView.addItems: no valid instance to append items with',
            items
          );
        } else {
          instance.addItems(items);
        }
      },
      getItem(id: string) {
        if (instance == null) {
          console.error('CodeView.getItem: no valid instance exists', id);
          return undefined;
        }
        return instance.getItem(id);
      },
      updateItem(item: CodeViewItem) {
        assertUncontrolledCodeViewAction(cache.controlled, 'updateItem');
        if (instance == null) {
          console.error(
            'CodeView.updateItem: no valid instance to update item with',
            item
          );
          return false;
        }
        return instance.updateItem(item);
      },
      updateItemId(oldId: string, newId: string) {
        assertUncontrolledCodeViewAction(cache.controlled, 'updateItemId');
        if (instance == null) {
          console.error(
            'CodeView.updateItemId: no valid instance to update item id with',
            oldId,
            newId
          );
          return false;
        }
        return instance.updateItemId(oldId, newId);
      },
      scrollTo(target: CodeViewScrollTarget) {
        if (instance == null) {
          console.error(
            'CodeView.scrollTo: no valid instance to scroll with',
            target
          );
        } else {
          instance.scrollTo(target);
        }
      },
      setSelectedLines(selection: CodeViewLineSelection | null) {
        if (instance == null) {
          console.error(
            'CodeView.setSelectedLines: no valid instance to update selection with',
            selection
          );
        } else {
          instance.setSelectedLines(selection, { notify: false });
          emit('selectedLinesChange', selection);
        }
      },
      getSelectedLines() {
        if (instance == null) {
          console.error('CodeView.getSelectedLines: no valid instance exists');
          return null;
        }
        return instance.getSelectedLines();
      },
      clearSelectedLines() {
        if (instance == null) {
          console.error(
            'CodeView.clearSelectedLines: no valid instance to update selection with'
          );
        } else {
          instance.clearSelectedLines({ notify: false });
          emit('selectedLinesChange', null);
        }
      },
      getInstance() {
        return instance;
      },
    });

    // The scroll container the core sets up, plus one `<Teleport>` per rendered
    // item that projects its slot content into the item's `<diffs-container>`.
    // `inheritAttrs` is disabled so we forward class/style/etc. onto the
    // container explicitly (the teleports must not receive them).
    return () => [
      h('div', { ref: containerRef, ...attrs }),
      ...snapshot.value.map((rendered) =>
        h(
          Teleport,
          { to: rendered.element, key: rendered.id },
          renderCodeViewItemChildren(rendered, slots)
        )
      ),
    ];
  },
});

// See the note on `FileDiff` for why this explicit annotation/cast is required.
export const CodeView =
  CodeViewImpl as unknown as DefineComponent<CodeViewPropsVue>;

// Build the light-DOM slot children for one rendered CodeView item. Each scoped
// slot receives the owning `item` so a single set of slots can render every
// file/diff in the list.
function renderCodeViewItemChildren(
  rendered: CodeViewRenderedItem<undefined>,
  slots: Slots
): VNodeChild[] {
  const { item, instance } = rendered;
  const getHoveredLine = () => instance.getHoveredLine();
  const children: VNodeChild[] = [];

  if (slots.header != null) {
    children.push(
      h('div', { slot: CUSTOM_HEADER_SLOT_ID }, slots.header({ item }))
    );
  } else {
    if (slots.headerPrefix != null) {
      children.push(
        h('div', { slot: HEADER_PREFIX_SLOT_ID }, slots.headerPrefix({ item }))
      );
    }
    if (slots.headerMetadata != null) {
      children.push(
        h(
          'div',
          { slot: HEADER_METADATA_SLOT_ID },
          slots.headerMetadata({ item })
        )
      );
    }
  }

  const annotations = item.annotations;
  if (slots.annotation != null && annotations != null) {
    const renderAnnotation = slots.annotation;
    annotations.forEach((annotation, index) => {
      children.push(
        h(
          'div',
          { key: index, slot: getLineAnnotationName(annotation) },
          renderAnnotation({ annotation, item })
        )
      );
    });
  }

  if (slots.gutter != null) {
    children.push(
      h(
        'div',
        { slot: 'gutter-utility-slot', style: GutterUtilitySlotStyles },
        slots.gutter({ getHoveredLine, item })
      )
    );
  }

  return children;
}

interface CreateManagedCodeViewOptionsParams {
  options: CodeViewOptions<undefined> | undefined;
  hasCustomHeader: boolean;
  hasGutterRenderer: boolean;
  onSelectedLinesChange(selection: CodeViewLineSelection | null): void;
  controlledSelection: boolean;
}

function createManagedCodeViewOptions({
  options,
  hasCustomHeader,
  hasGutterRenderer,
  onSelectedLinesChange,
  controlledSelection,
}: CreateManagedCodeViewOptionsParams): CodeViewOptions<undefined> | undefined {
  if (!hasCustomHeader && !hasGutterRenderer && !controlledSelection) {
    return options != null
      ? { ...options, onSelectedLinesChange }
      : { onSelectedLinesChange };
  }
  const next: CodeViewOptions<undefined> = {
    ...options,
    controlledSelection,
    onSelectedLinesChange,
  };
  if (hasCustomHeader) {
    next.renderCustomHeader = noopRender;
  }
  if (hasGutterRenderer) {
    next.renderGutterUtility = noopRender;
  }
  return next;
}

function areItemListsEqual(
  previousItems: readonly CodeViewItem[] | undefined,
  nextItems: readonly CodeViewItem[]
): boolean {
  if (previousItems == null || previousItems.length !== nextItems.length) {
    return false;
  }
  for (let index = 0; index < previousItems.length; index++) {
    if (previousItems[index] !== nextItems[index]) {
      return false;
    }
  }
  return true;
}

function isAppendOnlyItemUpdate(
  previousItems: readonly CodeViewItem[] | undefined,
  nextItems: readonly CodeViewItem[]
): previousItems is readonly CodeViewItem[] {
  if (previousItems == null || nextItems.length <= previousItems.length) {
    return false;
  }
  if (previousItems.length === 0) {
    return true;
  }
  for (let index = 0; index < previousItems.length; index++) {
    if (nextItems[index] !== previousItems[index]) {
      return false;
    }
  }
  return true;
}

function assertUncontrolledCodeViewAction(
  controlled: boolean | undefined,
  action: string
): void {
  if (controlled !== true) {
    return;
  }
  throw new Error(
    `CodeView.${action} cannot be used when CodeView is controlled. Use initialItems for imperative item updates.`
  );
}
